/**
 * DevTools - Development shortcuts for testing
 *
 * Features:
 * - Teleport button: Jump Blobo to next zone for easier testing
 */

import Phaser from 'phaser';
import { Blobo } from '../entities/Blobo';
import { Platform } from '../entities/Platform';

interface ZoneBoundary {
  name: string;
  startHeight: number; // Level coordinates (Y=0 at bottom)
  endHeight: number;
  useNearestPlatform?: boolean; // For victory zone, use nearest platform to startHeight
}

export class DevTools {
  private scene: Phaser.Scene;
  private blobo: Blobo;
  private platforms: Platform[];

  // Zone boundaries (in level coordinates: Y=0 at bottom, climbing up)
  private zones: ZoneBoundary[] = [
    { name: 'Tutorial', startHeight: 0, endHeight: 2000 },
    { name: 'Early Climb', startHeight: 2000, endHeight: 4000 },
    { name: 'Mid Climb', startHeight: 4000, endHeight: 6500 },
    { name: 'Late Climb', startHeight: 6500, endHeight: 8500 },
    { name: 'Expert Summit', startHeight: 8500, endHeight: 10000 },
    { name: 'Ultimate Challenge', startHeight: 10000, endHeight: 11400 },
    { name: 'Victory Zone', startHeight: 11400, endHeight: 12725, useNearestPlatform: true },
  ];

  private readonly WORLD_HEIGHT = 12725; // Match extended world height with victory zone

  constructor(scene: Phaser.Scene, blobo: Blobo, platforms: Platform[]) {
    this.scene = scene;
    this.blobo = blobo;
    this.platforms = platforms;
  }

  /**
   * Teleport Blobo to the next zone (called by UI button)
   */
  public teleportToNextZone(): void {
    const bloboPos = this.blobo.getPosition();

    // Convert Phaser Y (Y=0 at top) to level Y (Y=0 at bottom)
    const levelY = this.WORLD_HEIGHT - bloboPos.y;

    // Find current zone
    const currentZoneIndex = this.zones.findIndex(
      zone => levelY >= zone.startHeight && levelY < zone.endHeight
    );

    if (currentZoneIndex === -1) {
      console.log('[DevTools] Blobo not in any zone?');
      return;
    }

    // Get next zone (wrap to beginning if at end)
    const nextZoneIndex = (currentZoneIndex + 1) % this.zones.length;
    const nextZone = this.zones[nextZoneIndex];

    if (!nextZone) {
      console.log('[DevTools] No next zone found');
      return;
    }

    const currentZone = this.zones[currentZoneIndex];
    if (currentZone) {
      console.log(`[DevTools] Teleporting from ${currentZone.name} to ${nextZone.name}`);
    }

    // For Victory Zone, teleport to platform near the podium
    let targetPlatform: Platform | null = null;
    if (nextZone.useNearestPlatform) {
      targetPlatform = this.findPlatformNearHeight(nextZone.startHeight);
    } else {
      // Find first platform in next zone
      targetPlatform = this.findFirstPlatformInZone(nextZone);
    }

    if (targetPlatform) {
      this.teleportBlobo(targetPlatform);
    } else {
      console.log(`[DevTools] No platform found in ${nextZone.name}`);
    }
  }

  /**
   * Find platform nearest to a specific height (for victory zone teleport)
   */
  private findPlatformNearHeight(targetLevelY: number): Platform | null {
    // Get all platforms and their level Y coordinates
    const platformsWithLevelY = this.platforms.map(platform => {
      const body = platform.getBody();
      const gameY = body.position.y;
      const levelY = this.WORLD_HEIGHT - gameY;
      const distance = Math.abs(levelY - targetLevelY);
      return { platform, levelY, distance };
    });

    // Sort by distance to target height
    platformsWithLevelY.sort((a, b) => a.distance - b.distance);

    const nearest = platformsWithLevelY[0];
    return nearest ? nearest.platform : null;
  }

  /**
   * Find the first (lowest) platform in a zone
   */
  private findFirstPlatformInZone(zone: ZoneBoundary): Platform | null {
    // Get all platforms and their level Y coordinates
    const platformsWithLevelY = this.platforms.map(platform => {
      const body = platform.getBody();
      const gameY = body.position.y;
      const levelY = this.WORLD_HEIGHT - gameY;
      return { platform, levelY };
    });

    // Filter platforms in this zone
    const platformsInZone = platformsWithLevelY.filter(
      p => p.levelY >= zone.startHeight && p.levelY < zone.endHeight
    );

    if (platformsInZone.length === 0) {
      return null;
    }

    // Sort by levelY (ascending) to get the lowest platform in the zone
    platformsInZone.sort((a, b) => a.levelY - b.levelY);

    const firstPlatform = platformsInZone[0];
    return firstPlatform ? firstPlatform.platform : null;
  }

  /**
   * Teleport Blobo to a platform
   */
  private teleportBlobo(platform: Platform): void {
    const platformBody = platform.getBody();
    const platformY = platformBody.position.y;
    const platformX = platformBody.position.x;

    // Calculate spawn position on top of platform
    const bloboRadius = 15; // BLOBO.RADIUS from constants
    const platformHeight = 20; // Approximate platform height
    const spawnY = platformY - platformHeight / 2 - bloboRadius;

    // Get Blobo's body and teleport
    const bloboBody = this.blobo.getBody();
    this.scene.matter.body.setPosition(bloboBody, {
      x: platformX,
      y: spawnY,
    });

    // Reset velocity
    this.scene.matter.body.setVelocity(bloboBody, { x: 0, y: 0 });
    this.scene.matter.body.setAngularVelocity(bloboBody, 0);

    // Show visual feedback
    this.showTeleportEffect(platformX, spawnY);

    console.log(`[DevTools] Teleported Blobo to (${platformX.toFixed(0)}, ${spawnY.toFixed(0)})`);
  }

  /**
   * Show visual effect when teleporting
   */
  private showTeleportEffect(x: number, y: number): void {
    // Create a green flash effect for teleport
    const circle = this.scene.add.circle(x, y, 30, 0x00ff00, 0.5);
    circle.setDepth(100);

    this.scene.tweens.add({
      targets: circle,
      scaleX: 3,
      scaleY: 3,
      alpha: 0,
      duration: 500,
      ease: 'Power2',
      onComplete: () => {
        circle.destroy();
      },
    });
  }
}
